import { StyleSheet, Text, View, type StyleProp, type ViewStyle } from 'react-native';

import { colors, fonts, fontSizes } from '@/constants/theme';

type Size = 'sm' | 'base' | 'lg' | 'hero';
type Tone = 'onLight' | 'onDark';

export interface MoneyDisplayProps {
  /** Amount in cents. We never pass floats; backend gives us integer cents. */
  cents: number;
  size?: Size;
  tone?: Tone;
  /** Trailing unit like "/post" or "/day". Optional. */
  unit?: string;
  /** Show ".XX" cents portion. Default: hide if zero, show otherwise. */
  showCents?: boolean;
  style?: StyleProp<ViewStyle>;
}

/**
 * Money in MarketPryce's display style: dollar sign in Electric Green
 * (or Deep Green on light surfaces), whole-dollar amount in Druk Wide
 * tabular numerals, optional cents in muted small text, optional unit
 * suffix. Never use floats — pass integer cents.
 */
export function MoneyDisplay({
  cents,
  size = 'base',
  tone = 'onLight',
  unit,
  showCents,
  style,
}: MoneyDisplayProps) {
  const dollars = Math.floor(Math.abs(cents) / 100);
  const cents00 = Math.abs(cents) % 100;
  const sign = cents < 0 ? '-' : '';
  const includeCents = showCents ?? cents00 !== 0;

  const sizing = SIZES[size];
  const palette = TONES[tone];

  return (
    <View style={[styles.row, style]}>
      <Text style={[styles.dollarSign, sizing.dollarSign, palette.dollarSign]}>
        {sign}$
      </Text>
      <Text style={[styles.amount, sizing.amount, palette.amount]}>
        {formatThousands(dollars)}
      </Text>
      {includeCents && (
        <Text style={[styles.cents, sizing.cents, palette.cents]}>
          .{cents00.toString().padStart(2, '0')}
        </Text>
      )}
      {unit && (
        <Text style={[styles.unit, sizing.unit, palette.unit]}>
          {unit}
        </Text>
      )}
    </View>
  );
}

function formatThousands(n: number): string {
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

const SIZES = {
  sm: {
    dollarSign: { fontSize: fontSizes.base },
    amount: { fontSize: fontSizes.lg },
    cents: { fontSize: fontSizes.xs },
    unit: { fontSize: fontSizes.xs },
  },
  base: {
    dollarSign: { fontSize: fontSizes.lg },
    amount: { fontSize: fontSizes['2xl'] },
    cents: { fontSize: fontSizes.sm },
    unit: { fontSize: fontSizes.sm },
  },
  lg: {
    dollarSign: { fontSize: fontSizes['2xl'] },
    amount: { fontSize: fontSizes['4xl'] },
    cents: { fontSize: fontSizes.base },
    unit: { fontSize: fontSizes.sm },
  },
  hero: {
    // Hero sizing is hand-picked to match handoff `home.jsx` —
    // 28/56/14 lands the $ + amount as a single visual unit at the
    // proportions the brand designer drew. Don't snap these to the
    // fontSizes scale; the scale doesn't have intermediate values.
    dollarSign: { fontSize: 28 },
    amount: { fontSize: 56 },
    cents: { fontSize: 14 },
    unit: { fontSize: fontSizes.sm },
  },
} as const;

// Production marketpryce.com is restrained on Electric Green — uses it
// rarely, mostly Deep Green + Frost. Dollar sign matches the amount color
// so the price reads as one glyph cluster instead of a colored prefix +
// number. To bring back the brand-kit's Electric Green dollar accent,
// swap the dollarSign.color tokens here.
const TONES = {
  onLight: {
    dollarSign: { color: colors.deepGreen },
    amount: { color: colors.deepGreen },
    cents: { color: colors.textMuted },
    unit: { color: colors.textMuted },
  },
  onDark: {
    dollarSign: { color: colors.frost },
    amount: { color: colors.frost },
    cents: { color: colors.textOnDarkSubtle },
    unit: { color: colors.textOnDarkSubtle },
  },
} as const;

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 2,
  },
  dollarSign: {
    fontFamily: fonts.numeric,
    letterSpacing: -0.5,
  },
  amount: {
    fontFamily: fonts.numeric,
    fontVariant: ['tabular-nums'],
    letterSpacing: -1,
  },
  cents: {
    fontFamily: fonts.body,
    marginLeft: 2,
  },
  unit: {
    fontFamily: fonts.body,
    marginLeft: 4,
  },
});
